import { IVoiceProps } from '@/entities/Voice'
import { INewsProps } from '@/entities/News'
import { microCmsClient } from '@/infra/network/Microcms/APIClient'
import MicrocmsGateway from './index'
import { MicroCmsCache } from '@/infra/cache/MicroCmsCache'

export class CachedMicrocmsGateway extends MicrocmsGateway {
  private cache = new MicroCmsCache()

  // キャッシュTTL設定（環境変数で制御可能）
  private readonly cacheTTL = {
    featuredNews: parseInt(process.env.MICROCMS_CACHE_FEATURED_NEWS_TTL_MS || '3600000'), // デフォルト1時間
    newsList: parseInt(process.env.MICROCMS_CACHE_NEWS_LIST_TTL_MS || '604800000'), // デフォルト7日間
    news: parseInt(process.env.MICROCMS_CACHE_NEWS_TTL_MS || '604800000'), // デフォルト7日間
    voices: parseInt(process.env.MICROCMS_CACHE_VOICES_TTL_MS || '604800000'), // デフォルト7日間
    voice: parseInt(process.env.MICROCMS_CACHE_VOICE_TTL_MS || '604800000') // デフォルト7日間
  }

  // デバッグログ制御（開発環境のみ）
  private readonly debugEnabled = process.env.NODE_ENV !== 'production'

  private debugLog(message: string): void {
    if (this.debugEnabled) {
      console.log(message)
    }
  }

  constructor(client: typeof microCmsClient) {
    super(client)
  }

  private async checkLastModified(endpoint: string): Promise<number> {
    try {
      const response = await this.client.getList({
        endpoint,
        queries: {
          fields: 'id,updatedAt',
          limit: 1,
          orders: '-updatedAt'
        }
      })

      if (response.contents && response.contents.length > 0) {
        return new Date(response.contents[0].updatedAt).getTime()
      }
      return 0
    } catch (error) {
      console.warn(`Failed to check last modified for ${endpoint}:`, error)
      return 0
    }
  }

  private async checkCacheValidity(endpoint: string, cacheKey: string): Promise<boolean> {
    const cachedTime = this.cache.getCachedTime(cacheKey)
    if (!cachedTime) return false

    const lastModified = await this.checkLastModified(endpoint)
    return lastModified <= cachedTime
  }

  // 緊急性重視: 長時間キャッシュ + 更新チェック
  async getFeaturedNews(): Promise<INewsProps | null> {
    const key = 'featured_news'
    const cached = this.cache.get<INewsProps | null>(key)

    // キャッシュが存在するかチェック（nullもキャッシュされた値として扱う）
    const hasCache = this.cache.getCachedTime(key) !== null

    if (hasCache) {
      // キャッシュバリデーション
      const isValid = await this.checkCacheValidity('featured_news', key)
      if (isValid) {
        this.debugLog('MicroCMS Cache Hit: featured_news')
        return cached
      } else {
        this.debugLog('MicroCMS Cache Invalidated: featured_news')
        this.cache.clear(key)
      }
    }

    this.debugLog('MicroCMS Cache Miss: featured_news')
    const fresh = await super.getFeaturedNews()
    this.cache.set(key, fresh, this.cacheTTL.featuredNews)
    return fresh
  }

  // ほとんど更新されない: 長時間キャッシュ + 更新チェック
  async getNewsList(params: { page?: number; perPage?: number; today: string | null }): Promise<{ total: number; newsList: INewsProps[] }> {
    const keyParams = { page: params.page, perPage: params.perPage }
    const key = `news_list_${JSON.stringify(keyParams)}`
    const cached = this.cache.get<{ total: number; newsList: INewsProps[] }>(key)

    if (cached) {
      // キャッシュバリデーション
      const isValid = await this.checkCacheValidity('news', key)
      if (isValid) {
        this.debugLog('MicroCMS Cache Hit: news_list')
        return cached
      } else {
        this.debugLog('MicroCMS Cache Invalidated: news_list')
        this.cache.clear(key)
      }
    }

    this.debugLog('MicroCMS Cache Miss: news_list')
    const fresh = await super.getNewsList(params)
    this.cache.set(key, fresh, this.cacheTTL.newsList)
    return fresh
  }

  async getNews(newsId: string): Promise<INewsProps> {
    const key = `news_${newsId}`
    const cached = this.cache.get<INewsProps>(key)

    if (cached) {
      // キャッシュバリデーション
      const isValid = await this.checkCacheValidity('news', key)
      if (isValid) {
        this.debugLog(`MicroCMS Cache Hit: news_${newsId}`)
        return cached
      } else {
        this.debugLog(`MicroCMS Cache Invalidated: news_${newsId}`)
        this.cache.clear(key)
      }
    }

    this.debugLog(`MicroCMS Cache Miss: news_${newsId}`)
    const fresh = await super.getNews(newsId)
    this.cache.set(key, fresh, this.cacheTTL.news)
    return fresh
  }

  async getVoices(params: { page?: number; perPage?: number }): Promise<{ total: number; voices: IVoiceProps[] }> {
    const key = `voices_${JSON.stringify(params)}`
    const cached = this.cache.get<{ total: number; voices: IVoiceProps[] }>(key)

    if (cached) {
      // キャッシュバリデーション
      const isValid = await this.checkCacheValidity('voice', key)
      if (isValid) {
        this.debugLog('MicroCMS Cache Hit: voices')
        return cached
      } else {
        this.debugLog('MicroCMS Cache Invalidated: voices')
        this.cache.clear(key)
      }
    }

    this.debugLog('MicroCMS Cache Miss: voices')
    const fresh = await super.getVoices(params)
    this.cache.set(key, fresh, this.cacheTTL.voices)
    return fresh
  }

  async getVoice(voiceId: string): Promise<IVoiceProps> {
    const key = `voice_${voiceId}`
    const cached = this.cache.get<IVoiceProps>(key)

    if (cached) {
      // キャッシュバリデーション
      const isValid = await this.checkCacheValidity('voice', key)
      if (isValid) {
        this.debugLog(`MicroCMS Cache Hit: voice_${voiceId}`)
        return cached
      } else {
        this.debugLog(`MicroCMS Cache Invalidated: voice_${voiceId}`)
        this.cache.clear(key)
      }
    }

    this.debugLog(`MicroCMS Cache Miss: voice_${voiceId}`)
    const fresh = await super.getVoice(voiceId)
    this.cache.set(key, fresh, this.cacheTTL.voice)
    return fresh
  }

  // 緊急時用: 全キャッシュクリア
  clearAllCache(): void {
    this.cache.clear()
    this.debugLog('All microcms cache cleared')
  }

  // デバッグ用: キャッシュ状態確認
  getCacheStats(): { total: number; keys: string[] } {
    return this.cache.getStats()
  }
}

export const CachedMicrocmsGatewayFactory = (): CachedMicrocmsGateway => {
  return new CachedMicrocmsGateway(microCmsClient)
}
